import { Form, Formik } from "formik";
import React, { FC, useEffect, useState } from "react";
import Modal from 'react-modal';
import { toast } from "react-toastify";
import { useLocale } from "../../../../contexts/localeContext";
import { TranslationKeys } from "../../../../contexts/TranslationKeys";
import { BiometricWizardType } from "../../../../models/Biometric";
import { Step } from "../../../../models/Step";
import { installBiometric } from "../../../../services/installService";
import { getErrorMessage } from "../../../../services/utilities/ServiceError";
import { useDialog } from "../../../dialog/DialogContext";
import Wizard from "../../../wizard/wizard";
import SelectDevice from './SelectDevice';
import { SelectScanFilters } from "./SelectScanFilters";
import Summary from "./Summary";

interface InstallBiometricModalProps {
    isOpen: boolean;
    onClose: () => void;
    postInstall: () => void;
}

const InstallBiometricModal: FC<InstallBiometricModalProps> = ({ isOpen, onClose, postInstall }) => {
    const { translate } = useLocale();
    const [currentStep, setCurrentStep] = useState(0);
    const [steps, setSteps] = useState<Step[]>([]);
    const initialBiometricDevice = {
        id: 0,
        name: '',
        address: '',
        baAddress: '',
        ipAddress: '',
        serialAddress: '',
        startIp: '',
        endIp: '',
        biometricType: ''
    };
    const inititalBrand = {
        id: null,
        name: '',
        subCommand: '',
    };
    const [selectedDevice, setSelectedDevice] = useState<BiometricWizardType>(initialBiometricDevice);
    const [selectedBrand, setSelectedBrand] = useState<any>(inititalBrand);
    const [biometricDevicesToInstall, setBiometricDevicesToInstall] = useState<any>([]);
    const dialog = useDialog();

    // steps:
    // 0 - select bio-type & network
    // 1 - select device
    // 2 - summary
    useEffect(() => {
        const newSteps = new Array(0);

        // device type selected
        if (currentStep > 0 || selectedBrand.name) {
            newSteps.push({
                title: translate.byKey(TranslationKeys.selectBrand),
                description: selectedBrand.name,
                substeps: []
            });
        }
        else {
            newSteps.push({
                title: translate.byKey(TranslationKeys.selectBrand),
                description: translate.byKey(TranslationKeys.pending),
                substeps: []
            });
        }

        // network scanned, device selected
        if (currentStep > 1) {
            newSteps.push({
                title: translate.byKey(TranslationKeys.selectDevice),
                description: biometricDevicesToInstall.length + " " + translate.byKey(TranslationKeys.selected),
                substeps: []
            });
        }
        else {
            newSteps.push({
                title: translate.byKey(TranslationKeys.selectDevice),
                description: translate.byKey(TranslationKeys.pending),
                substeps: []
            });
        }

        // summary
        newSteps.push({
            title: translate.byKey(TranslationKeys.summary),
            description: translate.byKey(TranslationKeys.pending),
            substeps: []
        });

        setSteps(newSteps);
    }, [currentStep, biometricDevicesToInstall])

    const handleSelectDeviceBack = (e) => {
        setCurrentStep(currentStep - 1);
        setSelectedDevice({
            ...initialBiometricDevice,
            biometricType: selectedDevice.biometricType,
            startIp: selectedDevice.startIp,
            endIp: selectedDevice.endIp,
        })
    }

    const onBack = (e) => {
        setCurrentStep(currentStep - 1);
    }

    const onNext = (e) => {
        setCurrentStep(currentStep + 1);
    }

    const handleScanFiltersNext = (e) => {
        onNext(e);
    }

    const getStepContent = () => {
        return [
            <SelectScanFilters key={`installStep-0`}
                onNext={handleScanFiltersNext}
                setSelectedBrand={setSelectedBrand}
                selectedBrand={selectedBrand} />,
            <SelectDevice key={`installStep-1`}
                setSelectedBiometricDevices={setBiometricDevicesToInstall}
                onNext={onNext}
                onBack={handleSelectDeviceBack}
                selectedBrand={selectedBrand}/>,
            <Summary key={`installStep-2`}
                onBack={onBack} 
                selectedBiometricDevices={biometricDevicesToInstall}/>
        ];
    }

    const handleClose = () => {
        setCurrentStep(0);
        onClose();
    }

    const installDevice = () => {
        installBiometric(biometricDevicesToInstall)
            .then((success) => {
                handleClose();
                postInstall();
                toast.success(translate.byKey(TranslationKeys.installed))
            })
            .catch((failure) => {
                handleClose();
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) })
            });
    }

    return <>
        <Modal
            isOpen={isOpen}
            ariaHideApp={false}
            overlayClassName="flex w-screen position-float fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
            className="bg-white border-none m-auto shadow select-none outline-none w-content">
            <Formik
                enableReinitialize={true}
                initialValues={selectedDevice}
                onSubmit={installDevice}>
                <Form>
                    <Wizard
                        name={translate.byKey(TranslationKeys.installBiometric)}
                        step={currentStep}
                        onClose={handleClose}
                        sidebarSteps={steps}>
                        {getStepContent()}
                    </Wizard>
                </Form>
            </Formik>
        </Modal>
    </>
}

export default InstallBiometricModal;
